import { IconEdit, IconRefresh } from '@ui/src';
import { useAppSelector } from '../../../../../../../store/use-store';
import { ApplicationType } from '../../../../hoc/new-application/types';
import { AmlChangeLogDetailsTypes, AmlChangeLogValueRowTypes } from '../types';
import {
  ClientScoreTypeEnum,
  OrganizationType,
  AmlAction,
  RiskScoreRange,
} from '../../../../hoc/common/types';

import cn from '../styles.module.css';

const getRowRiskLevel = ({
  riskScoreRanges,
  totalRiskScore,
}: {
  riskScoreRanges?: RiskScoreRange[];
  totalRiskScore: number;
}) => {
  let totalRiskClassName: string;

  const riskLevel = riskScoreRanges?.find(
    (item) =>
      totalRiskScore >= item.rangeStart && totalRiskScore <= item.rangeEnd,
  );

  if (riskLevel?.action === AmlAction.NONE) {
    totalRiskClassName = cn.totalRiskScoringGood;
  } else if (riskLevel?.action === AmlAction.OPS_REVIEW) {
    totalRiskClassName = cn.totalRiskScoringMedium;
  } else {
    totalRiskClassName = cn.totalRiskScoringLow;
  }

  return totalRiskClassName;
};

const AmlChangeLogValueRow = ({
  title,
  value,
  score,
  additionalInfo,
}: AmlChangeLogValueRowTypes) => (
  <>
    <div
      className={`${cn.amlChangeLogValueRow} ${
        additionalInfo && additionalInfo?.length > 0 ? cn.withoutBorder : ''
      }`}
    >
      <div className={cn.amlChangeLogValueRowLeft}>
        {title && <div className={cn.amlChangeLogValueRowTitle}>{title}</div>}
        <div
          className={`${cn.amlChangeLogValueRowValue} ${
            !title ? cn.fullHeight : ''
          }`}
        >
          {value}
        </div>
      </div>
      <div className={cn.amlChangeLogValueRowScore}>{score}</div>
    </div>
    {additionalInfo &&
      additionalInfo?.length > 0 &&
      additionalInfo?.map((item, index) => (
        <div
          className={`${cn.amlChangeLogValueRow} ${
            cn.amlChangeLogValueRowAdditional
          } ${index + 1 === additionalInfo?.length ? cn.withBorderBottom : ''}`}
          key={item.id}
        >
          <div className={cn.amlChangeLogValueRowLeft}>
            <div className={`${cn.amlChangeLogValueRowValue} ${cn.fullHeight}`}>
              {item.name}
            </div>
          </div>
          <div className={cn.amlChangeLogValueRowScore}>{item.value}</div>
        </div>
      ))}
  </>
);

const AmlChangeLogDetails = ({
  changeLog,
  type,
  className,
  riskLevel,
}: AmlChangeLogDetailsTypes) => {
  const {
    riskScoreRanges,
    clientsActivities,
    deliveryChannelsIndividual,
    deliveryChannelsCorporate,
    productsCorporate,
    productsIndividual,
    incorporationDates,
    sicCodesDictionaries,
    sourcesOfFunds,
    monthlyTurnover,
    monthlyTurnoverCorporate,
  } = useAppSelector((state) => state.customersDictionaries);
  const { countriesList: countries } = useAppSelector((state) => state.root);

  const currentIncorporationDateYear = Math.max(
    ...(incorporationDates
      ?.filter(
        (item) => Number(changeLog?.dateOfIncorporation) >= Number(item.suffix),
      )
      .map((item) => Number(item.suffix)) as number[]),
  );

  return (
    <div className={cn.changeLogsDetailsItem}>
      <div className={cn.amlChangeLogDetailsTop}>
        <div className={`${cn.amlChangeLogDetailValue} ${className}`}>
          {changeLog?.totalRiskScore}
        </div>
        <div className={`${cn.amlChangeLogDetailLevel} ${className}`}>{`${
          riskLevel?.name.toLowerCase() || 'High'
        } Risk`}</div>
      </div>
      <div className={cn.amlChangeLogTypeWrapper}>
        <div className={cn.amlChangeLogType}>
          {changeLog?.clientScoreType === ClientScoreTypeEnum.AUTO ? (
            <span>
              <IconRefresh width={14} height={14} /> auto
            </span>
          ) : (
            <span>
              <IconEdit width={14} height={14} /> manually
            </span>
          )}
        </div>
      </div>

      {changeLog.clientScoreType !== ClientScoreTypeEnum.MANUAL && (
        <>
          <div className={cn.amlChangeLogDetailTitle}>
            <div className={cn.amlChangeLogDetailTitleText}>
              Geographical risk
            </div>
            <div
              className={`${cn.amlChangeLogDetailTitleScore} ${getRowRiskLevel({
                riskScoreRanges,
                totalRiskScore: changeLog.geographicalRiskTotal,
              })}`}
            >
              {changeLog.geographicalRiskTotal || '-'}
            </div>
          </div>
          {(type === ApplicationType.INDIVIDUAL ||
            type === OrganizationType.INDIVIDUAL) && (
            <>
              <AmlChangeLogValueRow
                title="Residency:"
                value={
                  (countries?.find((item) => item.value === changeLog.countryId)
                    ?.name as string) || '-'
                }
                score={changeLog.countryRisk || '-'}
              />
              <AmlChangeLogValueRow
                title="Citizenship:"
                value={
                  (countries?.find(
                    (item) => item.value === changeLog.nationalityId,
                  )?.name as string) || '-'
                }
                score={changeLog.nationalityRisk || '-'}
              />
            </>
          )}
          {(type === ApplicationType.CORPORATE ||
            type === OrganizationType.CORPORATE) && (
            <>
              <AmlChangeLogValueRow
                title="Country of incorporation:"
                value={
                  (countries?.find(
                    (item) => item.value === changeLog.countryOfIncorporationId,
                  )?.name as string) || '-'
                }
                score={changeLog.countryOfIncorporationRisk || '-'}
              />
              <AmlChangeLogValueRow
                title="Citizenship of company officials"
                value={
                  (countries?.find(
                    (item) => item.value === changeLog.countryOfIncorporationId,
                  )?.name as string) || '-'
                }
                score={changeLog.officialRiskTotal || '-'}
                additionalInfo={changeLog?.officialRiskItems}
              />
            </>
          )}
          {(type === ApplicationType.INDIVIDUAL ||
            type === OrganizationType.INDIVIDUAL) && (
            <>
              <div className={cn.amlChangeLogDetailTitle}>
                <div className={cn.amlChangeLogDetailTitleText}>
                  Type of client
                </div>
                <div
                  className={`${
                    cn.amlChangeLogDetailTitleScore
                  } ${getRowRiskLevel({
                    riskScoreRanges,
                    totalRiskScore: changeLog.clientActivityRisk,
                  })}`}
                >
                  {changeLog.clientActivityRisk || '-'}
                </div>
              </div>
              <AmlChangeLogValueRow
                title="Activity of the client:"
                value={
                  (clientsActivities?.find(
                    (item) => item.value === changeLog.clientActivityId,
                  )?.name as string) || '-'
                }
                score={changeLog.clientActivityRisk || '-'}
              />
            </>
          )}
          <div className={cn.amlChangeLogDetailTitle}>
            <div className={cn.amlChangeLogDetailTitleText}>
              Delivery channel
            </div>
            <div
              className={`${cn.amlChangeLogDetailTitleScore} ${getRowRiskLevel({
                riskScoreRanges,
                totalRiskScore: changeLog.deliveryChannelRisk,
              })}`}
            >
              {changeLog.deliveryChannelRisk || '-'}
            </div>
          </div>
          <AmlChangeLogValueRow
            value={
              type === OrganizationType.INDIVIDUAL ||
              type === ApplicationType.INDIVIDUAL
                ? deliveryChannelsIndividual?.find(
                    (item) => item.value === changeLog.deliveryChannelId,
                  )?.name || '-'
                : deliveryChannelsCorporate?.find(
                    (item) => item.value === changeLog.deliveryChannelId,
                  )?.name || '-'
            }
            score={changeLog.deliveryChannelRisk || '-'}
          />
          <div className={cn.amlChangeLogDetailTitle}>
            <div className={cn.amlChangeLogDetailTitleText}>Products</div>
            <div
              className={`${cn.amlChangeLogDetailTitleScore} ${getRowRiskLevel({
                riskScoreRanges,
                totalRiskScore: changeLog.productsRiskTotal,
              })}`}
            >
              {changeLog.productsRiskTotal || '-'}
            </div>
          </div>
          {changeLog.productsRiskItems?.map((item) => (
            <AmlChangeLogValueRow
              key={item.id}
              value={
                type === OrganizationType.INDIVIDUAL ||
                type === ApplicationType.INDIVIDUAL
                  ? productsIndividual?.find(
                      (product) => product.value === item.id,
                    )?.name || '-'
                  : productsCorporate?.find(
                      (product) => product.value === item.id,
                    )?.name || '-'
              }
              score={item.value || '-'}
            />
          ))}
          {(type === ApplicationType.CORPORATE ||
            type === OrganizationType.CORPORATE) && (
            <>
              <div className={cn.amlChangeLogDetailTitle}>
                <div className={cn.amlChangeLogDetailTitleText}>
                  Date of incorporation
                </div>
                <div
                  className={`${
                    cn.amlChangeLogDetailTitleScore
                  } ${getRowRiskLevel({
                    riskScoreRanges,
                    totalRiskScore: changeLog.dateOfIncorporationRisk,
                  })}`}
                >
                  {changeLog.dateOfIncorporationRisk || '-'}
                </div>
              </div>
              <AmlChangeLogValueRow
                value={
                  incorporationDates?.find(
                    (item) =>
                      Number(item.suffix) === currentIncorporationDateYear,
                  )?.name || '-'
                }
                score={changeLog.dateOfIncorporationRisk || '-'}
              />
              <div className={cn.amlChangeLogDetailTitle}>
                <div className={cn.amlChangeLogDetailTitleText}>Sic Codes</div>
                <div
                  className={`${
                    cn.amlChangeLogDetailTitleScore
                  } ${getRowRiskLevel({
                    riskScoreRanges,
                    totalRiskScore: changeLog.sicRiskTotal,
                  })}`}
                >
                  {changeLog.sicRiskTotal || '-'}
                </div>
              </div>
              {changeLog?.sicRiskItems?.map((item) => (
                <AmlChangeLogValueRow
                  key={item.name}
                  value={`${
                    sicCodesDictionaries?.find(
                      (sicCode) => sicCode.name === item.name,
                    )?.name as string
                  } - ${
                    sicCodesDictionaries?.find(
                      (sicCode) => sicCode.name === item.name,
                    )?.description as string
                  }`}
                  score={item.value || '-'}
                />
              ))}
            </>
          )}
          {(type === ApplicationType.INDIVIDUAL ||
            type === OrganizationType.INDIVIDUAL) && (
            <>
              <div className={cn.amlChangeLogDetailTitle}>
                <div className={cn.amlChangeLogDetailTitleText}>
                  Source of Funds
                </div>
                <div
                  className={`${
                    cn.amlChangeLogDetailTitleScore
                  } ${getRowRiskLevel({
                    riskScoreRanges,
                    totalRiskScore: changeLog.sourcesOfFundsRiskTotal,
                  })}`}
                >
                  {changeLog.sourcesOfFundsRiskTotal || '-'}
                </div>
              </div>
              {changeLog?.sourcesOfFundsRiskItems?.map((item) => (
                <AmlChangeLogValueRow
                  key={item.id}
                  value={
                    sourcesOfFunds?.find((source) => source.value === item.id)
                      ?.name as string
                  }
                  score={item.value || '-'}
                />
              ))}
            </>
          )}
          <div className={cn.amlChangeLogDetailTitle}>
            <div className={cn.amlChangeLogDetailTitleText}>Turnover</div>
            <div
              className={`${cn.amlChangeLogDetailTitleScore} ${getRowRiskLevel({
                riskScoreRanges,
                totalRiskScore: changeLog.monthlyTurnoverRangeRisk,
              })}`}
            >
              {changeLog.monthlyTurnoverRangeRisk || '-'}
            </div>
          </div>
          <AmlChangeLogValueRow
            title="Turnover:"
            value={
              type === OrganizationType.INDIVIDUAL ||
              type === ApplicationType.INDIVIDUAL
                ? monthlyTurnover?.find(
                    (item) => item.value === changeLog.monthlyTurnoverRangeId,
                  )?.name || '-'
                : monthlyTurnoverCorporate?.find(
                    (item) => item.value === changeLog.monthlyTurnoverRangeId,
                  )?.name || '-'
            }
            score={changeLog.monthlyTurnoverRangeRisk || '-'}
          />
        </>
      )}
    </div>
  );
};

export default AmlChangeLogDetails;
